<script setup lang="ts">
import Rating from "primevue/rating";
import Skeleton from "primevue/skeleton";
import { computed } from "vue";
import { ratingPt } from "./styles";
import type { SessionFieldsProps } from "./types";

// ── Props ──────────────────────────────────────────────────────────────────

const props = withDefaults(defineProps<SessionFieldsProps>(), {
  topics: () => [],
  summary: "",
  rating: 0,
  feedback: "",
  ratingLabel: "RATING",
});

const showInProgress = computed<boolean>(() => props.topics.length === 0 && !props.summary);
</script>

<template>
  <div class="flex flex-col gap-3">
    <div v-if="showInProgress" class="-mx-3.5 flex items-start gap-2 text-sm bg-teal-light px-3.5 py-2">
      <span class="material-symbols-outlined text-teal-700 text-base leading-5">schedule</span>
      <div>
        <div class="font-semibold text-indigo-base">Summary in progress</div>
        <div class="text-gray-500 mt-0.5">
          Topics and summary will appear after the session ends. Review the full conversation now with <span class="font-semibold text-indigo-base">View Detail.</span>
        </div>
      </div>
    </div>

    <div>
      <div class="text-[10px] font-bold tracking-widest text-gray-600 uppercase mb-0.5">TOPICS</div>
      <div v-if="showInProgress" class="flex gap-2">
        <Skeleton height="0.875rem" width="8rem"/>
        <Skeleton height="0.875rem" width="5rem"/>
      </div>
      <div v-else class="text-sm text-gray-900 font-medium leading-relaxed min-h-4">{{ topics.join(', ') }}</div>
    </div>

    <div>
      <div class="text-[10px] font-bold tracking-widest text-gray-600 uppercase mb-0.5">SUMMARY</div>
      <template v-if="showInProgress">
        <Skeleton height="0.875rem" width="85%" class="mb-1"/>
        <Skeleton height="0.875rem"/>
      </template>
      <div v-else class="text-sm text-gray-900 font-medium leading-relaxed min-h-4">{{ summary }}</div>
    </div>

    <div class="grid grid-cols-2 gap-3">
      <div>
        <div class="text-[10px] font-bold tracking-widest text-gray-600 uppercase mb-0.5">
          {{ ratingLabel }}
        </div>
        <Rating v-if="rating > 0" :modelValue="rating" :readonly="true" :cancel="false" :pt="ratingPt" />
        <div v-else class="text-sm text-gray-500 italic leading-relaxed">Not rated</div>
      </div>
      <div>
        <div class="text-[10px] font-bold tracking-widest text-gray-600 uppercase mb-0.5">FEEDBACK</div>
        <div v-if="feedback" class="text-sm text-gray-900 font-medium leading-relaxed">{{ feedback }}</div>
        <div v-else class="text-sm text-gray-500 italic leading-relaxed">
          {{ rating > 0 ? 'No written feedback' : 'No feedback provided' }}
        </div>
      </div>
    </div>
  </div>
</template>
